import { Component, OnInit, AfterViewInit, OnDestroy, Renderer2, ElementRef, HostListener } from '@angular/core';
import { Router, NavigationEnd, NavigationError } from '@angular/router';
import { LanguageService } from './shared/services/language.service';
import { Subject, filter, takeUntil } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'brocom';
  isInitialized = false;
  showBackToTop = false;
  navigationErrorUrl: string | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly languageReloadScrollKey = 'languageReloadScrollY';
  private pendingLanguageReloadScroll: number | null = null;
  private hasHandledNavigationEnd = false;

  constructor(
    private languageService: LanguageService,
    private router: Router,
    private renderer: Renderer2,
    private elementRef: ElementRef
  ) { }

  async ngOnInit(): Promise<void> {
    this.updateBackToTopVisibility();
    this.pendingLanguageReloadScroll = this.takeLanguageReloadScroll();
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd | NavigationError =>
            event instanceof NavigationEnd || event instanceof NavigationError,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        if (event instanceof NavigationError) {
          this.navigationErrorUrl = event.url;
          console.error('Navigation failed', event.error);
          return;
        }

        this.navigationErrorUrl = null;
        this.hasHandledNavigationEnd = true;
        if (this.pendingLanguageReloadScroll !== null) {
          this.restoreLanguageReloadScroll();
          return;
        }

        const url = event.urlAfterRedirects || event.url;
        const isProductFiltering = url.includes('/products') && url.includes('?');
        if (!isProductFiltering) {
          window.scrollTo(0, 0);
        }
      });

    try {
      const savedLanguage = localStorage.getItem('selectedLanguage');
      const targetLanguage = savedLanguage
        ? this.languageService.getSavedLanguagePreference()
        : (await this.languageService.detectLocationAndLanguage()).language;
      this.languageService.emitLanguageChange(targetLanguage);
    } catch (error) {
      console.error('Could not initialize the selected application language', error);
      this.languageService.emitLanguageChange('pl');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  retryFailedNavigation(): void {
    const failedUrl = this.navigationErrorUrl;
    if (!failedUrl) return;

    void this.router.navigateByUrl(failedUrl).catch((error: unknown) => {
      console.error('Could not retry the failed navigation', error);
      this.navigationErrorUrl = failedUrl;
    });
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.updateBackToTopVisibility();
  }

  scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }

  ngAfterViewInit(): void {
    void this.initializeApplication();
  }

  private updateBackToTopVisibility(): void {
    this.showBackToTop = window.scrollY > 400;
  }

  private async initializeApplication(): Promise<void> {
    let maximumWaitTimer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      this.waitForCriticalResources(),
      new Promise<void>((resolve) => {
        maximumWaitTimer = setTimeout(resolve, 2500);
      }),
    ]).finally(() => {
      if (maximumWaitTimer !== undefined) {
        clearTimeout(maximumWaitTimer);
      }
    });
    await new Promise<void>((resolve) => setTimeout(resolve, 150));

    this.isInitialized = true;
    const initialLoader = document.getElementById('initial-loader');
    if (initialLoader) {
      initialLoader.style.opacity = '0';
      setTimeout(() => {
        initialLoader.style.display = 'none';
      }, 300);
    }

    this.renderer.addClass(document.body, 'app-initialized');
    this.renderer.addClass(this.elementRef.nativeElement, 'initialized');
    if (this.hasHandledNavigationEnd) {
      this.restoreLanguageReloadScroll();
    }
  }

  private async waitForCriticalResources(): Promise<void> {
    try {
      const resources: Promise<unknown>[] = [this.waitForStylesheets()];
      if (document.fonts) {
        resources.push(document.fonts.ready);
      }
      await Promise.all(resources);
    } catch (error) {
      console.warn('Some page resources did not finish loading before initialization', error);
    }
  }

  private takeLanguageReloadScroll(): number | null {
    let savedScroll: string | null = null;
    try {
      savedScroll = sessionStorage.getItem(this.languageReloadScrollKey);
      sessionStorage.removeItem(this.languageReloadScrollKey);
    } catch (error) {
      console.error('Could not restore scroll position after language reload', error);
    }

    const scrollY = Number(savedScroll);
    if (savedScroll === null || !Number.isFinite(scrollY) || scrollY < 0) {
      return null;
    }

    return scrollY;
  }

  private restoreLanguageReloadScroll(): void {
    const scrollY = this.pendingLanguageReloadScroll;
    if (scrollY === null) {
      return;
    }

    this.pendingLanguageReloadScroll = null;
    requestAnimationFrame(() => window.scrollTo(0, scrollY));
  }

  private async waitForStylesheets(): Promise<void> {
    const hasPendingStylesheets = () =>
      Array.from(document.styleSheets).some((sheet) => {
        try {
          return sheet.cssRules.length === 0;
        } catch {
          return false;
        }
      });

    if (!hasPendingStylesheets()) {
      return;
    }

    await new Promise<void>((resolve) => {
      const finish = () => {
        window.removeEventListener('load', onLoad);
        clearTimeout(timeout);
        resolve();
      };
      const onLoad = () => finish();
      const timeout = setTimeout(finish, 500);
      window.addEventListener(
        'load',
        onLoad,
        { once: true },
      );
    });
  }
}
